import { useCallback, useRef, useState } from 'react';
import { ControlsCommonProps } from '../../fields';
import { OverlaySpinner } from 'components/Spinner';
import { Cover } from 'components/Cover';
import { PortalToDropdown } from "components/Portal/PortalToDropdown"
import { useSelectedItems } from './hooks/useSelectedItems';
import styles from './multiSelect.module.scss';
import { useControlContent } from './hooks/useControlContent';
import { Dropdown } from './Dropdown/Dropdown';
import { useFilteredItems } from './hooks/useFilteredItems';

export const multiselectId = 'multiselectId'

// Multiselect (like Textarea) accepts Enter
// so this method is used in Form to prevent auto save of the form via Enter key
export const isMultiSelectHit = (target: HTMLElement) => {
	return !!target.closest(`#${multiselectId}`);
}

export type MultiSelectProps = ControlsCommonProps<Array<string | number>> & {
	items?: any[]
	getItemId(item: any): string | number
	getItemText(item: any): string
	loading?: boolean
	sort?: boolean
	numOfItemNames?: number
	isLink?: boolean
	getItemLinkRoute?(item: any): string
	size?: 'medium' | 'small'
}

export const MultiSelect = (props: MultiSelectProps) => {
	const {
		value, onChange, onBlur, disabled,
		items = [], getItemId, getItemText, getItemLinkRoute,
		loading, sort, isLink, numOfItemNames = 1, size = 'medium'
	} = props;

	const containerRef = useRef<HTMLDivElement>(null);
	const [expanded, setExpanded] = useState(false);
	const [filterValue, setFilterValue] = useState<string>();

	const { selectedItems, remainingItems } = useSelectedItems({
		value,
		items,
		getItemId
	})

	const filteredItems = useFilteredItems({
		filterValue,
		remainingItems,
		getItemText,
		sort
	})

	const onChangeCallback = useCallback(
		(newSelectedItems: any[]) => {
			const ids = newSelectedItems.map(item => getItemId(item));
			onChange && onChange(ids);
		},
		[getItemId, onChange]
	)

	const onBlurCallback = useCallback(
		() => {
			setExpanded(false);
			setFilterValue(undefined);
			onBlur && onBlur();
		},
		[onBlur]
	)

	const controlContent = useControlContent({
		selectedItems,
		getItemId,
		getItemText,
		getItemLinkRoute,
		isLink,
		count: items.length,
		numOfItemNames,
		expanded,
		size,
		disabled
	})

	const expandCallback = useCallback(
		() => !disabled && !loading && setExpanded(true),
		[disabled, loading]
	)

	return (
		<div ref={containerRef} id={multiselectId} className={`${styles.container} ${size === 'small' ? styles.small : ''}`}>
			{expanded && <Cover onClick={onBlurCallback} transparent />}
			<div className={styles.select_container} onClick={expandCallback}>
				{/* control */}
				{controlContent}
				{/* dropdown */}
				{expanded &&
					<PortalToDropdown containerRef={containerRef}>
						<Dropdown
							selectedItems={selectedItems}
							filteredItems={filteredItems}
							filterItems={setFilterValue}
							getItemId={getItemId}
							getItemText={getItemText}
							onChange={onChangeCallback}
							disabled={!!disabled}
						/>
					</PortalToDropdown>
				}
				{/* loading */}
				{loading && <OverlaySpinner useBrandColor />}
			</div>
		</div>
	)
}
